/**
 * Consultas e gravações dos dois cadastros pelo TanStack Query.
 *
 * Papel: aqui ficam `useResidencias` e `usePessoasFamilia`, que mantêm em cache
 * o que a tela lê, e as quatro mutações que gravam e mandam a listagem
 * correspondente se atualizar.
 *
 * Como usar: toda chave deste módulo começa por `CHAVE_CADASTROS_BASE`, e cada
 * mutação invalida apenas o ramo que alterou — gravar uma casa não tem por que
 * fazer a lista de pessoas ser buscada de novo. Uma chave que não comece pelo
 * prefixo fica fora da invalidação e continua mostrando o dado antigo depois de
 * salvar.
 *
 * As duas consultas não usam `keepPreviousData`: não há paginação nem busca
 * digitada, então não existe página anterior que valha a pena manter na tela.
 */
import { type NovaPessoaFamilia, type NovaResidencia } from '@concierge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  atualizarPessoaFamilia,
  buscarPessoasFamilia,
  criarPessoaFamilia,
  excluirPessoaFamilia,
} from '../api/pessoas-familia';
import {
  atualizarResidencia,
  buscarResidencias,
  criarResidencia,
  excluirResidencia,
} from '../api/residencias';

/** Prefixo de todas as chaves de cache deste módulo. */
export const CHAVE_CADASTROS_BASE = ['cadastros-base'] as const;

/** Chave da lista de residências. */
export const CHAVE_RESIDENCIAS = [...CHAVE_CADASTROS_BASE, 'residencias'] as const;

/** Chave da lista de pessoas da família. */
export const CHAVE_PESSOAS_FAMILIA = [...CHAVE_CADASTROS_BASE, 'pessoas-familia'] as const;

/**
 * Lê as residências cadastradas.
 *
 * @returns Resultado da consulta, com as casas na ordem da API: agrupadas pelo
 *   nome do lugar que as contém, com as que não estão dentro de nada primeiro,
 *   e pelo nome dentro de cada grupo. A primeira da lista não é, por isso, uma
 *   escolha padrão para tela nenhuma — as regras de `casas.ts` é que decidem.
 */
export function useResidencias() {
  return useQuery({
    queryKey: CHAVE_RESIDENCIAS,
    queryFn: ({ signal }) => buscarResidencias(signal),
  });
}

/**
 * Lê as pessoas da família cadastradas.
 *
 * @returns Resultado da consulta, em ordem alfabética de nome completo.
 */
export function usePessoasFamilia() {
  return useQuery({
    queryKey: CHAVE_PESSOAS_FAMILIA,
    queryFn: ({ signal }) => buscarPessoasFamilia(signal),
  });
}

/** Variáveis de `useSalvarResidencia`. */
interface SalvarResidenciaVariaveis {
  /** Identificador da casa a alterar, ou `null` para criar uma nova. */
  id: string | null;
  dados: NovaResidencia;
}

/**
 * Cria ou altera uma residência, conforme venha identificador.
 *
 * O `mutateAsync` rejeita com `ErroDaApi` quando a API recusa, e o formulário
 * precisa tratar essa rejeição — 409 significa nome já cadastrado, e é a
 * resposta esperada para a segunda grafia da mesma casa.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a gravação não responde.
 */
export function useSalvarResidencia() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dados }: SalvarResidenciaVariaveis) =>
      id === null ? criarResidencia(dados) : atualizarResidencia(id, dados),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_RESIDENCIAS }),
  });
}

/**
 * Apaga uma residência.
 *
 * Rejeita com 409 quando a casa ainda é apontada por outro cadastro, e nesse
 * caso nada foi apagado — a tela precisa dizer isso a quem tentou.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a exclusão não responde.
 */
export function useExcluirResidencia() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => excluirResidencia(id),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_RESIDENCIAS }),
  });
}

/** Variáveis de `useSalvarPessoaFamilia`. */
interface SalvarPessoaVariaveis {
  /** Identificador da pessoa a alterar, ou `null` para criar uma nova. */
  id: string | null;
  dados: NovaPessoaFamilia;
}

/**
 * Cria ou altera uma pessoa da família, conforme venha identificador.
 *
 * O `mutateAsync` rejeita com `ErroDaApi` quando a API recusa, e o formulário
 * precisa tratar essa rejeição — 409 significa nome completo já cadastrado, e é
 * a resposta esperada para quem tenta separar em duas linhas alguém que já está
 * no cadastro.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a gravação não responde.
 */
export function useSalvarPessoaFamilia() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dados }: SalvarPessoaVariaveis) =>
      id === null ? criarPessoaFamilia(dados) : atualizarPessoaFamilia(id, dados),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_PESSOAS_FAMILIA }),
  });
}

/**
 * Apaga uma pessoa da família.
 *
 * Rejeita com 409 quando a pessoa ainda é apontada por documento ou plano de
 * saúde, e nesse caso nada foi apagado.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a exclusão não responde.
 */
export function useExcluirPessoaFamilia() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => excluirPessoaFamilia(id),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_PESSOAS_FAMILIA }),
  });
}
